import type { IntlShape } from 'react-intl';

import { Context, Effect, Exit, Match, Option, Schema, type SchemaAST, SchemaIssue } from 'effect';

import { IntlContext } from './intl/IntlContext';

/**
 * Issue shape used by project formatters. v4's standard-schema-v1 formatter
 * emits only `{ message, path }` — the v3 `_tag` discriminator is gone, since
 * one-issue-per-leaf no longer needs a kind tag. The `tag` argument to
 * {@link createError} is kept for readability at call-sites but no longer
 * lands in the output.
 */
export type ArrayFormatterIssue = {
  readonly message: string;
  readonly path: ReadonlyArray<PropertyKey>;
};

export const createError = (
  _tag: 'Type' | 'Refinement' | 'Transformation' | 'Missing',
  message: string,
  path: ReadonlyArray<PropertyKey> = []
): ArrayFormatterIssue => ({
  message,
  path,
});

export const createMessage = (closure: (intl: IntlShape) => string, defaultMessage = '') =>
  Effect.gen(function* () {
    const service = yield* Effect.serviceOption(IntlContext);
    return Option.match(service, {
      onNone: () => defaultMessage,
      onSome: (messages) => closure(messages.intl),
    });
  });

export const wrapInApostrophe = (value: string): string => `„${value}“`;

export const formatUnknown = (value: unknown): string => {
  const formatted = Match.value(value).pipe(
    Match.when(Match.number, (n) => n.toString()),
    Match.when(Match.null, () => `null`),
    Match.when(Match.undefined, () => `undefined`),
    Match.when(Match.string, (s) => `"${s}"`),
    Match.orElse(() => JSON.stringify(value))
  );

  return wrapInApostrophe(formatted);
};

// `Effect.runSync` requires `R = never`, so the schema's decode/encode
// services must be reducible to never. `Schema.Codec<T, E>` defaults
// `RD = RE = never`, which gives the cleanest inferred type for callers
// that pass project-defined schemas built with plain `Schema.Struct` etc.

export const decodeUnknownEitherWithFormatter =
  <T, E>(schema: Schema.Codec<T, E>, options: SchemaAST.ParseOptions = { errors: 'all' }) =>
  (value: unknown) => {
    return Schema.decodeUnknownExit(schema)(value, options).pipe(
      Exit.mapError((error) => SchemaIssue.makeFormatterStandardSchemaV1()(error.issue).issues)
    );
  };

export const prepareDecodeUnknownEitherWithFormatterIntl =
  (intl: IntlShape) =>
  <T, E>(schema: Schema.Codec<T, E>, options: SchemaAST.ParseOptions = { errors: 'all' }) =>
  (value: unknown) => {
    const context = Context.empty().pipe(Context.add(IntlContext, { intl: intl }));

    return Schema.decodeUnknownEffect(schema)(value, options).pipe(
      Effect.mapError((error) => SchemaIssue.makeFormatterStandardSchemaV1()(error.issue).issues),
      Effect.result,
      Effect.provide(context),
      Effect.runSync
    );
  };

export const prepareEncodeUnknownEitherWithFormatterIntl =
  (intl: IntlShape) =>
  <T, E>(schema: Schema.Codec<T, E>, options: SchemaAST.ParseOptions = { errors: 'all' }) =>
  (value: unknown) => {
    const context = Context.empty().pipe(Context.add(IntlContext, { intl: intl }));

    return Schema.encodeUnknownEffect(schema)(value, options).pipe(
      Effect.mapError((error) => SchemaIssue.makeFormatterStandardSchemaV1()(error.issue).issues),
      Effect.result,
      Effect.provide(context),
      Effect.runSync
    );
  };
